import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'

import { cn } from '../../lib/utils'
import { TOKENS } from '../ui/dashboard-primitives'

/**
 * The one composition behind every dead-end screen: route error, 404, and the
 * top-level `ErrorBoundary`.
 *
 * SHAPE COMES FROM THE CORPUS, not from invention. Aboard, Mercor, Maze,
 * Charma and Contractbook all ship the same skeleton for a page that failed:
 * a short label, a headline, ONE line of body, and a single primary action,
 * with anything secondary visibly quieter. Nothing on this screen competes
 * with the way out.
 *
 * CENTRED, against the usual anti-centre bias, because this app already
 * centres its dead-end screens: `SessionEmptyState` is `text-center` and it is
 * the closest thing in the codebase to what this is. Matching it matters more
 * than a general rule about hero sections, and Coinbase's in-shell 404 does
 * the same.
 *
 * ONE EYEBROW COLOUR, NOT A SEMANTIC ONE, AND THE NUMBERS DECIDED IT. The
 * error variant used to be terracotta, following DESIGN.md's "terracotta for
 * error states" -- but that line is written for icons and for chips sitting on
 * an opaque `--warm-dark` fill (see `DevSessionBadge`), not for 9px text on the
 * raw page gradient. Measured against the gradient's tan peak at 54%, which is
 * exactly where a centred eyebrow lands: terracotta on that background is
 * **1.39:1**, which is not a weak contrast, it is an invisible one. The screen
 * whose entire job is to work when everything else has failed was signalling
 * failure in a colour nobody could see.
 *
 * `creamSoft` measures 3.09:1 there and 4.63:1 across the rest of the width. It
 * is the app's own body token, so this borrows an existing value rather than
 * inventing one, and it stays visually quiet. STILL SHORT OF AA's 4.5:1 at the
 * single worst column: that gap belongs to 9px type over a gradient and is
 * app-wide, not something these two screens get to fix alone. The heading, at
 * `cream`, measures 4.37 to 11.33 and carries the meaning regardless.
 *
 * Losing the colour costs nothing the corpus was relying on: Aboard, Mercor,
 * Maze and Charma all label a failed page in plain neutral type.
 *
 * TYPE ONLY, NO ILLUSTRATION. `SessionEmptyState` draws Yami Kuro, so the
 * mascot would not be out of place here, but where the mascot appears is a
 * decision René takes personally (Spec 2 stage 3b, and 007-A24 refused to
 * settle a smaller version of the same question). A new screen does not get to
 * settle it by default. The eyebrow carries the signal instead, in terracotta
 * for a failure and dim cream for a plain miss.
 *
 * The caller owns the outer height: a route error replaces the whole shell and
 * passes `min-h-[100dvh]`, while the 404 renders inside `<main>` beside the
 * rail and just needs padding.
 */
interface Props {
  eyebrow: string
  title: string
  body: string
  /** Primary first. See `ErrorActions`. */
  actions?: ReactNode
  /** Developer detail, gated by the caller. Gets the full column width. */
  detail?: ReactNode
  className?: string
}

export function ErrorState({
  eyebrow,
  title,
  body,
  actions,
  detail,
  className,
}: Props) {
  // Entrance only, and only just. An error screen should arrive calmly rather
  // than perform: one fade-up over the whole block, no stagger. Reduced motion
  // keeps the fade and drops the travel, which is what the guidance asks for
  // rather than removing the transition outright.
  const reduce = useReducedMotion()

  return (
    <div className={cn('flex w-full items-center justify-center px-6', className)}>
      <motion.div
        initial={{ opacity: 0, y: reduce ? 0 : 8 }}
        animate={{ opacity: 1, y: 0 }}
        // DESIGN.md's dramatic-enter curve, at the short end of its range.
        transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-[560px]"
      >
        <div className="text-center">
          <div
            className="text-[9px] uppercase tracking-[0.26em]"
            style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamSoft }}
          >
            {eyebrow}
          </div>
          <h1
            className="mt-3 text-[26px] font-medium tracking-[-0.02em]"
            style={{ fontFamily: 'var(--font-display)', color: TOKENS.cream }}
          >
            {title}
          </h1>
          <p
            className="mx-auto mt-2.5 max-w-[44ch] text-[13px] leading-relaxed"
            style={{ color: TOKENS.creamSoft }}
          >
            {body}
          </p>
          {actions && (
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">{actions}</div>
          )}
        </div>
        {detail}
      </motion.div>
    </div>
  )
}
